/**
 * Guide — in-app User Guide at /guide. Fetches USER_GUIDE.md from the backend
 * and renders it with a sticky table of contents built from the headings.
 */
import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { api } from "@/lib/api";
import { BookOpen, Loader2 } from "lucide-react";

export const slugify = (text) =>
  String(text).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const headingText = (children) =>
  React.Children.toArray(children).map((c) => (typeof c === "string" ? c : c?.props?.children || "")).join("");

export const mdComponents = {
  h1: ({ children }) => <h1 id={slugify(headingText(children))} className="font-heading text-3xl font-black tracking-tight mt-2 mb-4 scroll-mt-20">{children}</h1>,
  h2: ({ children }) => <h2 id={slugify(headingText(children))} className="font-heading text-xl font-bold tracking-tight mt-10 mb-3 pb-2 border-b border-zinc-200 scroll-mt-20">{children}</h2>,
  h3: ({ children }) => <h3 id={slugify(headingText(children))} className="font-heading text-base font-bold mt-6 mb-2 scroll-mt-20">{children}</h3>,
  p: ({ children }) => <p className="text-sm text-zinc-700 leading-relaxed mb-3">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 mb-3 space-y-1 text-sm text-zinc-700">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-3 space-y-1 text-sm text-zinc-700">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ href, children }) => <a href={href} className="text-blue-700 font-medium hover:underline">{children}</a>,
  strong: ({ children }) => <strong className="font-bold text-zinc-950">{children}</strong>,
  code: ({ children }) => <code className="bg-zinc-100 border border-zinc-200 rounded px-1 py-0.5 text-[12px] font-mono">{children}</code>,
  pre: ({ children }) => <pre className="bg-zinc-950 text-zinc-100 rounded-sm p-4 text-xs overflow-x-auto mb-4">{children}</pre>,
  hr: () => <hr className="my-8 border-zinc-200" />,
  table: ({ children }) => <div className="overflow-x-auto mb-4"><table className="w-full text-sm border border-zinc-200">{children}</table></div>,
  thead: ({ children }) => <thead className="bg-zinc-50">{children}</thead>,
  th: ({ children }) => <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-600 border-b-2 border-zinc-950">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 border-b border-zinc-100 text-zinc-700 align-top">{children}</td>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-amber-400 bg-amber-50 px-4 py-2 mb-3 text-sm">{children}</blockquote>,
};

export default function Guide() {
  const [md, setMd] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/guide").then((r) => setMd(r.data?.markdown || "")).catch(() => setError("Could not load the guide."));
  }, []);

  const toc = useMemo(() => {
    const rows = [];
    for (const line of md.split("\n")) {
      const m = line.match(/^(#{1,2})\s+(.*)/);
      if (m) rows.push({ depth: m[1].length, text: m[2].replace(/[*`]/g, ""), id: slugify(m[2].replace(/[*`]/g, "")) });
    }
    return rows.filter((r) => r.depth === 2 || rows.length < 40);
  }, [md]);

  if (error) return <div className="p-8 text-sm text-red-700">{error}</div>;
  if (!md) return <div className="p-8 flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading guide…</div>;

  return (
    <div className="p-6 sm:p-8 animate-in fade-in duration-500" data-testid="guide-page">
      <div className="flex items-end justify-between mb-6 pb-6 border-b border-zinc-200 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700 mb-2 flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Help</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">User Guide</h1>
          <div className="text-sm text-zinc-600 mt-1">The full project lifecycle, step by step — from new rep to close-out.</div>
        </div>
      </div>
      <div className="flex gap-8 items-start">
        {/* TOC */}
        <aside className="hidden lg:block w-64 shrink-0 sticky top-6 max-h-[85vh] overflow-y-auto border border-zinc-200 bg-white rounded-sm p-4" data-testid="guide-toc">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">On this page</div>
          <nav className="space-y-1">
            {toc.map((h, i) => (
              <button
                key={i}
                onClick={() => document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="block w-full text-left text-xs text-zinc-600 hover:text-blue-700 hover:underline leading-snug py-0.5"
              >
                {h.text}
              </button>
            ))}
          </nav>
        </aside>
        {/* Content */}
        <article className="flex-1 min-w-0 bg-white border border-zinc-200 rounded-sm p-6 sm:p-10 max-w-4xl" data-testid="guide-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={mdComponents}>
            {md}
          </ReactMarkdown>
        </article>
      </div>
    </div>
  );
}
